import axiosInstance from '../../axios'

const state = {
  invoiceData: null,
  processing: false,
  error: null
}

const getters = {
  invoiceData: state => state.invoiceData,
  isProcessing: state => state.processing,
  error: state => state.error
}

const actions = {
  // Phase 1: Extract text and get refined estimate
  async extractText({ commit }, file) {
    commit('SET_ERROR', null);

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axiosInstance.post('/api/extract-text', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      })

      return {
        success: true,
        extraction_id: response.data.extraction_id,
        char_count: response.data.char_count,
        refined_estimate: response.data.refined_estimate,
        extraction_method: response.data.extraction_method,
        num_pages: response.data.num_pages
      }
    } catch (error) {
      const errorMessage = error.response ? error.response.data.error : 'Failed to extract text'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  // Phase 2: Process invoice data
  async processInvoice({ commit, dispatch }, extraction_id) {
    commit('SET_PROCESSING', true);
    commit('SET_ERROR', null);

    try {
      const response = await axiosInstance.post('/api/process-invoice', {
        extraction_id: extraction_id
      }, {
        withCredentials: true
      })

      commit('SET_INVOICE_DATA', response.data.invoice_data)

      // Update credits in auth store
      // Refresh user data from server to ensure credits are in sync
      dispatch('auth/fetchUser', null, { root: true })

      return {
        success: true,
        message: response.data.message,
        creditsUsed: response.data.credits_used,
        creditsRemaining: response.data.credits_remaining,
        processing_time: response.data.processing_time,
        extraction_method: response.data.extraction_method,
        char_count: response.data.char_count
      }
    } catch (error) {
      const errorMessage = error.response ? error.response.data.error : 'Failed to process invoice'
      commit('SET_ERROR', errorMessage)
      // If backend provided a download_url for the failure, include it so UI can surface it
      const downloadUrl = error.response && error.response.data && error.response.data.download_url ? error.response.data.download_url : null
      return { success: false, error: errorMessage, download_url: downloadUrl }
    } finally {
      commit('SET_PROCESSING', false)
    }
  },

  // Legacy single-phase method (for backward compatibility)
  async processPdf({ commit, dispatch }, file) {
    commit('SET_PROCESSING', true);
    commit('SET_ERROR', null);

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axiosInstance.post('/api/process-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      })

      commit('SET_INVOICE_DATA', response.data.invoice_data)

      // Update credits in auth store
      // Refresh user data from server to ensure credits are in sync
      dispatch('auth/fetchUser', null, { root: true })

      return {
        success: true,
        message: response.data.message,
        creditsUsed: response.data.credits_used,
        creditsRemaining: response.data.credits_remaining,
        processing_time: response.data.processing_time,
        extraction_method: response.data.extraction_method,
        char_count: response.data.char_count,
        refined_estimate: response.data.refined_estimate
      }
    } catch (error) {
      const errorMessage = error.response ? error.response.data.error : 'Failed to process PDF'
      commit('SET_ERROR', errorMessage)
      const downloadUrl = error.response && error.response.data && error.response.data.download_url ? error.response.data.download_url : null
      return { success: false, error: errorMessage, download_url: downloadUrl }
    } finally {
      commit('SET_PROCESSING', false)
    }
  },

  async getInvoiceData({ commit }) {
    try {
      const response = await axiosInstance.get('/api/invoice-data', { withCredentials: true })
      commit('SET_INVOICE_DATA', response.data)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response ? error.response.data.error : 'Failed to get invoice data' }
    }
  },

  async updateInvoiceData({ commit }, data) {
    try {
      await axiosInstance.post('/api/invoice-data', data, { withCredentials: true })
      commit('SET_INVOICE_DATA', data)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response ? error.response.data.error : 'Failed to update invoice data' }
    }
  },

  clearInvoiceData({ commit }) {
    commit('SET_INVOICE_DATA', null)
  }
}

const mutations = {
  SET_INVOICE_DATA(state, data) {
    state.invoiceData = data
  },

  SET_PROCESSING(state, status) {
    state.processing = status
  },

  SET_ERROR(state, error) {
    state.error = error
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}